import React from 'react'

import { getMintPrice, normalizePrice } from '@common/functions'
import useStore from '@store/networkStore'
import useStoreNetworkWallet from '@store/networkWalletStore'
import { INetworkContextData } from '@types'

import useMain from './useMain'

export default function useNetwork() {
    const {
        state: {
            contract,
            mainInit: { contractAddress, socialMedias, network, blockchain, contractNameTitle },
            selectedSalesBehaviour: {
                type,
                priceMethod,
                mintMethod,
                batchMintMethod,
                assetUrl,
                mintPrice,
                startDate,
                dutchAuction,
                maxPool,
            },
        },
    } = useMain()

    const { networkManager } = useStoreNetworkWallet()

    const isSoldOut = useStore((state: INetworkContextData) => state.isSoldOut)
    const setIsSoldOut = useStore((state: INetworkContextData) => state.setIsSoldOut)
    const stage = useStore((state: INetworkContextData) => state.stage)
    const setStage = useStore((state: INetworkContextData) => state.setStage)
    const currentContractAddress = useStore((state: INetworkContextData) => state.currentContractAddress)
    const setCurrentContractAddress = useStore((state: INetworkContextData) => state.setCurrentContractAddress)
    const contractName = useStore((state: INetworkContextData) => state.contractName)
    const setContractName = useStore((state: INetworkContextData) => state.setContractName)
    const currentMintPrice = useStore((state: INetworkContextData) => state.currentMintPrice)
    const setCurrentMintPrice = useStore((state: INetworkContextData) => state.setCurrentMintPrice)
    const selectedOption = useStore((state: INetworkContextData) => state.selectedOption)
    const setSelectedOption = useStore((state: INetworkContextData) => state.setSelectedOption)
    const remainedTokenCount = useStore((state: INetworkContextData) => state.remainedTokenCount)
    const setRemainedTokenCount = useStore((state: INetworkContextData) => state.setRemainedTokenCount)
    const totalSupply = useStore((state: INetworkContextData) => state.totalSupply)
    const setTotalSupply = useStore((state: INetworkContextData) => state.setTotalSupply)
    const reset = useStore((state: INetworkContextData) => state.reset)
    const handleChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedOption(Number(event.target.value))
    }, [])

    const noRenderAgain = React.useRef(true)

    const getRemainedTokenCount = React.useCallback(async () => {
        const remainingTokens = await networkManager.getRemainedTokenCount(
            contractAddress[currentContractAddress],
            setCurrentContractAddress,
            setIsSoldOut,
        )

        return { tokenIdCounter: remainingTokens, totalSupply: maxPool }
    }, [contractAddress, currentContractAddress, maxPool, networkManager, setCurrentContractAddress, setIsSoldOut])

    const tokenCount = async () => {
        const handleRemainedTokenCount = await getRemainedTokenCount()

        if (remainedTokenCount === -1 && handleRemainedTokenCount) {
            const { tokenIdCounter, totalSupply } = handleRemainedTokenCount
            setRemainedTokenCount(tokenIdCounter)
            setTotalSupply(totalSupply)
        }
    }
    const getContractName = React.useCallback(async () => {
        return networkManager.getContractName(contractAddress[currentContractAddress])
    }, [networkManager, contractAddress, currentContractAddress])

    const takeContractName = async () => {
        console.log(contractName)
        if (!contractNameTitle) {
            const handleContractName = await getContractName()

            if (handleContractName) {
                setContractName(handleContractName)
            }
        } else {
            setContractName(contractNameTitle)
        }
    }

    const handleMintPriceChange = async () => {
        if (mintPrice) {
            const price = await getMintPrice(mintPrice, startDate, dutchAuction)
            const currentPrice = normalizePrice(price, selectedOption)
            setCurrentMintPrice(currentPrice)
        }
    }

    return {
        state: {
            contract,
            contractAddress,
            socialMedias,
            assetUrl,
            network,
            blockchain,
            salesBehavior: { type, priceMethod, mintMethod, batchMintMethod, mintPrice },
            isSoldOut,
            stage,
            currentContractAddress,
            contractName,
            currentMintPrice,
            selectedOption,
            remainedTokenCount,
            totalSupply,
            noRenderAgain,
        },
        operations: {
            setIsSoldOut,
            setStage,
            setSelectedOption,
            setCurrentContractAddress,
            setContractName,
            setCurrentMintPrice,
            setRemainedTokenCount,
            setTotalSupply,
            handleChange,
            reset,
            tokenCount,
            handleMintPriceChange,
            takeContractName,
        },
    }
}
